import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { deactivateSignoffs } from './signoffDeactivation'
import { useTicketSignoffEntry, useWriteSignoffs } from './signoffsApi'
import { applySignature } from './signoffState'
import type { SignoffRecord, SignoffsField } from './signoffTypes'

export interface TicketSignoffsContextType {
  signoffs: SignoffsField
  isLoading: boolean
  sign: (slotId: string, record: SignoffRecord) => Promise<void>
  /** Clears every slot's `active` flag; signature history is kept. */
  deactivateAll: () => Promise<void>
}

const TicketSignoffsContext = createContext<TicketSignoffsContextType | null>(
  null
)

export const TicketSignoffsProvider: FC<{
  ticket: LibraTicket
  children: ReactNode
}> = ({ ticket, children }) => {
  const { signoffs, isLoading, forceRefresh } = useTicketSignoffEntry(ticket.id)
  const writeSignoffs = useWriteSignoffs()

  const write = useCallback(
    async (next: SignoffsField) => {
      await writeSignoffs(ticket, signoffs, next)
      await forceRefresh()
    },
    [writeSignoffs, ticket, signoffs, forceRefresh]
  )

  const sign = useCallback(
    (slotId: string, record: SignoffRecord) =>
      write(applySignature(signoffs, slotId, record)),
    [write, signoffs]
  )

  const deactivateAll = useCallback(
    () => write(deactivateSignoffs(signoffs)),
    [write, signoffs]
  )

  const value = useMemo<TicketSignoffsContextType>(
    () => ({ signoffs, isLoading, sign, deactivateAll }),
    [signoffs, isLoading, sign, deactivateAll]
  )

  return (
    <TicketSignoffsContext.Provider value={value}>
      {children}
    </TicketSignoffsContext.Provider>
  )
}

export const useTicketSignoffs = (): TicketSignoffsContextType => {
  const ctx = useContext(TicketSignoffsContext)
  if (!ctx) {
    throw new Error(
      'useTicketSignoffs must be used within a TicketSignoffsProvider'
    )
  }
  return ctx
}
